/**
 * Represents a reference to a child sub-group within a parent group.
 *
 * This is the SOURCE OF TRUTH for per-parent overrides when a sub-group
 * is shared across multiple parent groups via `parentIds`.
 *
 * Stored on the PARENT group's `subgroups[]` array. Each parent explicitly
 * declares pricing, availability, and visibility overrides for each child
 * sub-group. These overrides are SCOPED — they ONLY affect items within
 * that specific sub-group when accessed through that specific parent path.
 * They do NOT bubble up to affect the parent group itself, sibling
 * sub-groups, or any other branch of the tree.
 *
 * Follows the same reference model pattern as:
 * - ItemReference (menu group → item, with priceOverride)
 * - ModifierGroupReference (item → modifier group, with selection rules)
 * - MenuGroupReference (item → menu group, with pricing context)
 *
 * Supports:
 * - Sub-group ordering within the parent (position)
 * - Visibility override per parent context (isHidden, channel visibility)
 * - Service-type availability override (dine-in, takeout, etc.)
 * - Scheduling overrides (daysAvailable, dateRange, seasonalAvailability)
 * - Pricing overrides (groupPricingStrategy, fixed price, size-based)
 */

import type {
  GroupPricingStrategy,
  IGroupSizePrice,
  IMenuGroupAvailability,
  IMenuGroupDateRange,
  IMenuGroupDaysAvailable,
  IMenuGroupSeasonalAvailability,
} from "./MenuGroup";

export interface ISubgroupReference {
  /** The menuGroupId of the child sub-group */
  readonly subgroupId: number;
  /**
   * Root/primary group scoping. When set, this override only applies
   * when navigating through the specified root group. When undefined,
   * applies to all roots (backward compatible fallback).
   */
  readonly rootGroupId?: number;
  /** Display position within this parent (0-based) */
  readonly position: number;

  // ── Visibility overrides ──
  /** Hide this sub-group under this specific parent */
  readonly isHidden?: boolean;
  /** Display name override for this parent context */
  readonly displayNameOverride?: string;
  /** Channel visibility overrides (null = use sub-group's own value) */
  readonly visibleInPOS?: boolean;
  readonly visibleInKiosk?: boolean;
  readonly visibleInOnlineOrdering?: boolean;
  readonly visibleToPartners?: boolean;
  readonly visibleInInvoices?: boolean;

  // ── Service-type availability override ──
  /** Override which service types this sub-group is available for under this parent */
  readonly availability?: IMenuGroupAvailability;

  // ── Scheduling overrides (each independently optional) ──
  /** Override days/time availability under this parent */
  readonly daysAvailable?: IMenuGroupDaysAvailable;
  /** Override date range availability under this parent */
  readonly dateRange?: IMenuGroupDateRange;
  /** Override seasonal availability under this parent */
  readonly seasonalAvailability?: IMenuGroupSeasonalAvailability;

  // ── Pricing overrides ──
  /** Override group pricing strategy under this parent. Default when absent: "off" */
  readonly groupPricingStrategyOverride?: GroupPricingStrategy;
  /** Fixed price when groupPricingStrategyOverride is "fixed" */
  readonly groupFixedPriceOverride?: number;
  /** Size tiers when groupPricingStrategyOverride is "size_based" */
  readonly groupSizePricesOverride?: IGroupSizePrice[];
}

export type ISubgroupReferenceData = Partial<ISubgroupReference>;

export class SubgroupReference implements ISubgroupReference {
  readonly subgroupId: number;
  readonly rootGroupId?: number;
  readonly position: number;

  // Visibility overrides
  readonly isHidden?: boolean;
  readonly displayNameOverride?: string;
  readonly visibleInPOS?: boolean;
  readonly visibleInKiosk?: boolean;
  readonly visibleInOnlineOrdering?: boolean;
  readonly visibleToPartners?: boolean;
  readonly visibleInInvoices?: boolean;

  // Service-type availability override
  readonly availability?: IMenuGroupAvailability;

  // Scheduling overrides
  readonly daysAvailable?: IMenuGroupDaysAvailable;
  readonly dateRange?: IMenuGroupDateRange;
  readonly seasonalAvailability?: IMenuGroupSeasonalAvailability;

  // Pricing overrides
  readonly groupPricingStrategyOverride?: GroupPricingStrategy;
  readonly groupFixedPriceOverride?: number;
  readonly groupSizePricesOverride?: IGroupSizePrice[];

  constructor(data: ISubgroupReferenceData) {
    this.subgroupId = data.subgroupId!;
    this.rootGroupId = data.rootGroupId;
    this.position = data.position ?? 0;

    // Visibility
    this.isHidden = data.isHidden;
    this.displayNameOverride = data.displayNameOverride;
    this.visibleInPOS = data.visibleInPOS;
    this.visibleInKiosk = data.visibleInKiosk;
    this.visibleInOnlineOrdering = data.visibleInOnlineOrdering;
    this.visibleToPartners = data.visibleToPartners;
    this.visibleInInvoices = data.visibleInInvoices;

    // Service-type availability
    this.availability = data.availability;

    // Scheduling
    this.daysAvailable = data.daysAvailable;
    this.dateRange = data.dateRange;
    this.seasonalAvailability = data.seasonalAvailability;

    // Pricing
    this.groupPricingStrategyOverride = data.groupPricingStrategyOverride;
    this.groupFixedPriceOverride = data.groupFixedPriceOverride;
    this.groupSizePricesOverride = data.groupSizePricesOverride;
  }

  toJSON(): ISubgroupReference {
    const result: ISubgroupReference = {
      subgroupId: this.subgroupId,
      position: this.position,
    };

    // Only include optional fields if they have values
    if (this.rootGroupId !== undefined) {
      (result as any).rootGroupId = this.rootGroupId;
    }
    if (this.isHidden !== undefined) {
      (result as any).isHidden = this.isHidden;
    }
    if (this.displayNameOverride !== undefined) {
      (result as any).displayNameOverride = this.displayNameOverride;
    }
    if (this.visibleInPOS !== undefined) {
      (result as any).visibleInPOS = this.visibleInPOS;
    }
    if (this.visibleInKiosk !== undefined) {
      (result as any).visibleInKiosk = this.visibleInKiosk;
    }
    if (this.visibleInOnlineOrdering !== undefined) {
      (result as any).visibleInOnlineOrdering = this.visibleInOnlineOrdering;
    }
    if (this.visibleToPartners !== undefined) {
      (result as any).visibleToPartners = this.visibleToPartners;
    }
    if (this.visibleInInvoices !== undefined) {
      (result as any).visibleInInvoices = this.visibleInInvoices;
    }
    if (this.availability !== undefined) {
      (result as any).availability = this.availability;
    }
    if (this.daysAvailable !== undefined) {
      (result as any).daysAvailable = this.daysAvailable;
    }
    if (this.dateRange !== undefined) {
      (result as any).dateRange = this.dateRange;
    }
    if (this.seasonalAvailability !== undefined) {
      (result as any).seasonalAvailability = this.seasonalAvailability;
    }
    if (this.groupPricingStrategyOverride !== undefined) {
      (result as any).groupPricingStrategyOverride = this.groupPricingStrategyOverride;
    }
    if (this.groupFixedPriceOverride !== undefined) {
      (result as any).groupFixedPriceOverride = this.groupFixedPriceOverride;
    }
    if (this.groupSizePricesOverride !== undefined) {
      (result as any).groupSizePricesOverride = this.groupSizePricesOverride;
    }

    return result;
  }

  static fromJSON(data: Record<string, unknown>): SubgroupReference {
    if (!data.subgroupId) throw new Error("SubgroupReference.fromJSON: subgroupId is missing.");
    // Provide default position of 0 if missing (for backward compatibility)
    const position = typeof data.position === "number" ? data.position : 0;

    return new SubgroupReference({
      subgroupId: data.subgroupId as number,
      rootGroupId: data.rootGroupId as number | undefined,
      position,
      isHidden: data.isHidden as boolean | undefined,
      displayNameOverride: data.displayNameOverride as string | undefined,
      visibleInPOS: data.visibleInPOS as boolean | undefined,
      visibleInKiosk: data.visibleInKiosk as boolean | undefined,
      visibleInOnlineOrdering: data.visibleInOnlineOrdering as boolean | undefined,
      visibleToPartners: data.visibleToPartners as boolean | undefined,
      visibleInInvoices: data.visibleInInvoices as boolean | undefined,
      availability: data.availability as IMenuGroupAvailability | undefined,
      daysAvailable: data.daysAvailable as IMenuGroupDaysAvailable | undefined,
      dateRange: data.dateRange as IMenuGroupDateRange | undefined,
      seasonalAvailability: data.seasonalAvailability as IMenuGroupSeasonalAvailability | undefined,
      groupPricingStrategyOverride: data.groupPricingStrategyOverride as GroupPricingStrategy | undefined,
      groupFixedPriceOverride: data.groupFixedPriceOverride as number | undefined,
      groupSizePricesOverride: data.groupSizePricesOverride as IGroupSizePrice[] | undefined,
    });
  }

  /**
   * Check if this sub-group is visible in this parent context.
   */
  isVisible(): boolean {
    return !this.isHidden;
  }

  /**
   * Get the effective pricing strategy for this sub-group under this parent.
   * Returns the override if set, otherwise "off" (items use their own pricing).
   */
  getEffectivePricingStrategy(): GroupPricingStrategy {
    return this.groupPricingStrategyOverride ?? "off";
  }

  /**
   * Find the best-matching SubgroupReference for a child within a parent's subgroups[].
   * Prefers a rootGroupId-scoped entry; falls back to unscoped (rootGroupId undefined).
   */
  static findForChild(
    subgroups: ISubgroupReference[],
    subgroupId: number,
    rootGroupId?: number,
  ): ISubgroupReference | undefined {
    if (rootGroupId != null) {
      const scoped = subgroups.find(
        (s) => s.subgroupId === subgroupId && s.rootGroupId === rootGroupId,
      );
      if (scoped) return scoped;
    }
    // Fallback: unscoped entry (rootGroupId is undefined/null)
    return subgroups.find(
      (s) => s.subgroupId === subgroupId && s.rootGroupId == null,
    );
  }
}
